<template>
  <div class="metals-ticker" @mouseenter="paused = true" @mouseleave="paused = false">
    <span v-if="metalsError" class="metals-ticker__error">Price data unavailable</span>
    <template v-else-if="metals.length">
      <div class="metals-ticker__track" ref="tickerTrack">
        <div
          v-for="n in tickerCopies"
          :key="n"
          class="metals-ticker__inner"
          :ref="n === 1 ? 'tickerInner' : null"
          :aria-hidden="n > 1 ? 'true' : null"
        >
          <span v-for="(m, i) in metals" :key="n + '-' + m.metalCode" class="metals-ticker__item">
            <span v-if="i > 0" class="metals-ticker__sep">|</span>
            <span class="metals-ticker__name">{{ m.metalName }}</span>
            <span class="metals-ticker__price">{{ formatPrice(m.pricePerOz) }}/oz</span>
            <span
              v-if="m.change24hPct !== null"
              class="metals-ticker__change"
              :class="m.change24hPct >= 0 ? 'up' : 'down'"
            >{{ m.change24hPct >= 0 ? '▲' : '▼' }}{{ Math.abs(m.change24hPct).toFixed(2) }}%</span>
          </span>
          <span class="metals-ticker__attr"><a href="https://izios.com" target="_blank" rel="noopener noreferrer">Powered by IZIOS</a></span>
        </div>
      </div>
    </template>
    <span v-else class="metals-ticker__loading">Loading prices…</span>
  </div>
  <v-app>
    <v-app-bar class="light-theme">
      <v-container 
        fluid
        height="100%"
        class="pa-0"
      >
        <v-row
          v-if="$vuetify.display.smAndDown"
          no-gutters
          class="justify-center mb-1"
        >
        <div class="text-right languages">
          <v-icon icon="mdi-web"></v-icon>
          Languages
        </div>
        </v-row>
        <v-row 
          no-gutters
          class="px-4"
        >
          <v-col 
            cols="6"
            sm="4"
          >
            <a href="/about">
              <v-img
                :src="logo"
                alt="logo"
                class="logo"
                contain
                max-width="366"
                :transition="false"
              />
            </a>
          </v-col>
          <v-col 
            v-if="!$vuetify.display.mobile"
            cols="7"
            offset="1"
            class="align-self-end"
          >
            <div class="text-right languages">
              <v-icon icon="mdi-web"></v-icon>
              Languages
            </div>
            <ul class="d-flex justify-space-between align-end">
              <li v-for="(link, i) in links" class="nav-item" :key="i">
                <a
                  v-if="!link.subLinks"
                  class="nav-link"
                  :href="$router.resolve({path: link.to}).href"
                >
                  {{ link.text }}
                </a>
                <v-menu
                  v-else
                  attach
                  eager
                  :offset-y="$vuetify.display.mdAndUp"
                  :open-on-hover="true"
                  open-delay="250"
                  transition="slide-y-transition"
                >
                  <template #activator="{props}">
                    <a
                      class="nav-link" 
                      :href="$router.resolve({path: link.to}).href"
                      v-bind="props"
                    >
                      {{ link.text }}
                    </a>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="(item, index) in link.subLinks"
                      :key="index"
                      :value="index"
                    >
                      <a
                        class="nav-link" 
                        :href="$router.resolve({path: item.to , query: item.query}).href"
                      >
                        {{ item.text }}
                      </a>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </li>
            </ul>
          </v-col>
          <v-col 
            v-if="$vuetify.display.mobile"
            class="justify-end align-center d-flex drawer"
          >
            <v-app-bar-nav-icon
              aria-label="Open Mobile Menu"
              @click.stop="drawer = !drawer"
              size="x-large"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-navigation-drawer
      v-if="$vuetify.display.mobile"
      v-model="drawer" 
      clipped
      width="240"
      sticky
    >
      <v-list
        flat 
        dense 
        nav 
        class="py-1"
      >
        <div v-for="(link, i) in links" :key="i">
          <v-list-group v-if="link.subLinks">
            <template #activator="{props}">
              <v-list-item v-bind="props">{{ link.text }}</v-list-item>
            </template>
            <v-list-item
              v-for="(sub, ii) in link.subLinks"
              :key="ii"
              dense
            >
              <a :href="$router.resolve({path: sub.to , query: sub.query}).href">
                {{ sub.text }}
              </a>
            </v-list-item>
          </v-list-group>
          <v-list-item
            v-else
            dense
            :title="link.text"
            :to="link.to"
          />
        </div> 
      </v-list>
    </v-navigation-drawer>
    <router-view class="content" />
    <Footer v-if="showFooter" />
  </v-app>
</template>

<script>
import logo from "@/assets/logo.png"
import Footer from "@/views/FooterPage.vue"

export default {
  components: {
    Footer
  },
  data() {
    return {
      showFooter: false,
      drawer: false,
      logo,
      links: [
        { to: '/about', text: 'About Us' },
        {
          to: '/products',
          text: 'Products',
          subLinks: [
            { text: 'New Products', to: '/products?newProducts=true', query: { new: 'true' } },
            { text: 'Findings',     to: '/products?cat=Findings',     query: { cat: 'Findings' } },
            { text: 'Chains',       to: '/products?cat=Chains',       query: { cat: 'Chains' } },
            { text: 'Mills',        to: '/products?cat=Mills',        query: { cat: 'Mills' } },
            { text: 'Inverness',    to: '/inverness' },
          ]
        },
        {
          to: '/about',
          text: 'Guide & Reference',
          subLinks: [
            { text: 'Conversion Tables',        to: '/conversion' },
            { text: 'Gold Filled Vs. Gold Plated', to: '/gold' },
            { text: 'LeachGarner Video',         to: '/videos' },
          ]
        },
        {
          to: '/news',
          text: 'News, Awards & Cert.',
          subLinks: [
            { text: 'News',           to: '/news' },
            { text: 'Awards',         to: '/awards' },
            { text: 'Certifications', to: '/certs' },
          ]
        },
        { to: '/contact', text: 'Contact Us' },
      ],
      metals: [],
      metalsError: false,
      paused: false,
      metalsTimer: null,
      tickerCopies: 2
    };
  },
  mounted() {
    setTimeout(() => {
      this.showFooter = true;
    }, 3000);

    this.fetchMetals();
    this.metalsTimer = setInterval(() => {
      if (!this.paused) this.fetchMetals();
    }, 5 * 60 * 1000);
  },
  beforeUnmount() {
    if (this.metalsTimer) clearInterval(this.metalsTimer);
    if (this._tickerRAF) cancelAnimationFrame(this._tickerRAF);
    if (this._tickerRO) this._tickerRO.disconnect();
  },
  methods: {
    startTicker() {
      if (this._tickerRAF) cancelAnimationFrame(this._tickerRAF);
      if (this._tickerRO) this._tickerRO.disconnect();

      this.$nextTick(() => {
        document.fonts.ready.then(() => {
          requestAnimationFrame(() => {
            const innerRef = this.$refs.tickerInner;
            const inner = Array.isArray(innerRef) ? innerRef[0] : innerRef;
            const track = this.$refs.tickerTrack;
            const container = track && track.parentElement;
            if (!inner || !track || !container) return;

            const w = inner.getBoundingClientRect().width;
            const containerW = container.getBoundingClientRect().width;

            // Guard: if width is 0 the element isn't painted yet — retry
            if (w === 0) {
              requestAnimationFrame(() => this.startTicker());
              return;
            }

            // Ensure enough copies so the screen is ALWAYS filled:
            // At x = -w the visible window is [w .. w+containerW].
            // We need total track width > w + containerW.
            // copies * w > w + containerW  =>  copies > 1 + containerW/w
            const needed = Math.ceil(containerW / w) + 2;
            if (needed !== this.tickerCopies) {
              this.tickerCopies = needed;
              // Re-enter after Vue has rendered the new copies
              this.$nextTick(() => this.startTicker());
              return;
            }

            let x = 0;
            let lastTs = null;
            const pxPerMs = w / 25000;

            const step = (ts) => {
              if (!this.paused) {
                if (lastTs !== null) {
                  x -= pxPerMs * (ts - lastTs);
                  if (x <= -w) x += w;
                }
                lastTs = ts;
                track.style.transform = `translateX(${x}px)`;
              } else {
                lastTs = ts;
              }
              this._tickerRAF = requestAnimationFrame(step);
            };
            this._tickerRAF = requestAnimationFrame(step);

            this._tickerRO = new ResizeObserver(() => {
              const newW = inner.getBoundingClientRect().width;
              if (Math.abs(newW - w) > 0.5) {
                this._tickerRO.disconnect();
                this.startTicker();
              }
            });
            this._tickerRO.observe(inner);
          });
        });
      });
    },
    async fetchMetals() {
      try {
        const res = await fetch('https://izios.com/api/metals/widget-data', {
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const json = await res.json();
        if (json.success && json.data) {
          this.metals = json.data;
          this.metalsError = false;
          this.startTicker();
        } else {
          this.metalsError = true;
        }
      } catch {
        this.metalsError = true;
      }
    },
    formatPrice(price) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(price);
    }
  }
};
</script>

<style>

/* ── Metals Ticker ── */
.metals-ticker {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: #ffffff;
  border-bottom: 1px solid #e7d8af;
  padding: 6px 16px 6px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 13px;
  color: #1e293b;
  box-sizing: border-box;
  z-index: 2001;
}
.metals-ticker__item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.metals-ticker__sep {
  color: #94a3b8;
  margin-right: 12px;
}
.metals-ticker__name {
  font-weight: 600;
  color: #be9b59;
}
.metals-ticker__price {
  font-weight: 500;
}
.metals-ticker__change {
  font-size: 11px;
  font-weight: 600;
}
.metals-ticker__change.up   { color: #10b981; }
.metals-ticker__change.down { color: #ef4444; }
.metals-ticker__attr {
  margin-left: auto;
  font-size: 9px;
}
.metals-ticker__attr a {
  color: #9ca3af;
  text-decoration: none;
}
.metals-ticker__loading,
.metals-ticker__error {
  font-size: 11px;
  color: #94a3b8;
  padding: 2px 0;
}
.metals-ticker__error { color: #ef4444; }
/* Hide extra copies on desktop — only first copy is shown */
.metals-ticker__inner + .metals-ticker__inner { display: none; }
/* Desktop: track fills the bar, inner behaves as the original flex row */
.metals-ticker__track {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}
.metals-ticker__inner {
  display: contents; /* lets items participate directly in the parent flex */
}

.light-theme {
  background-color: #ffffff; 
  color: #be9b59;
}

.v-app-bar.light-theme {
  z-index: 2000 !important;
  margin-top: 30px;
}

.light-theme .nav-link {
  color: #be9b59;
	font-size: 17px;
	display: inline-block;
	text-transform: capitalize;
	font-weight: 600;
}
.languages {
  color: #be9b59;
	font-size: 15px;
}

.light-theme .nav-link:hover {
  color: #7a643b;
}
.v-toolbar {
  padding: 10px 0;
}
.v-toolbar__content {
  height: 80px !important;
}

.navbar {
  list-style-type: none;
  height: 80px;
}

.bi {
  margin-right: 5px;
}
.content {
  margin-top: 143px !important;
  min-height: calc(100vh - 200px);
}
.v-layout {
  background-color: #f9fafc;
}
.v-menu > .v-overlay__content {
	top: 100px !important;
}

body {
  font-family: 'OpenSans', sans-serif;
  transition: background-color 0.3s, color 0.3s;
}
.title-text {
  color: #be9b59;
  font-size: 29px;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 1rem;
}

@media screen and (max-width: 960px) {
  .v-toolbar__content {
    height: 75px !important;
  }
  
  .v-list-item__content {
    font-size: 13px;
  }

  .content {
    margin-top: 75px !important;
  }
  .logo, .drawer {
    max-height: 50px;
  }
  .v-navigation-drawer {
    top: 125px !important;
  }

  /* ── Mobile scrolling ticker ── */
  .metals-ticker {
    overflow: hidden;
    padding: 6px 0;
    flex-wrap: nowrap;
  }
  .metals-ticker__track {
    display: flex;
    flex-wrap: nowrap;
    gap: 0;
    width: max-content;
    will-change: transform;
  }
  .metals-ticker__inner {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 0 24px;
    white-space: nowrap;
  }
  /* Show all copies on mobile for seamless loop */
  .metals-ticker__inner + .metals-ticker__inner { display: inline-flex; }
  .metals-ticker__attr {
    margin-left: 12px;
  }
}

.v-container--fluid {
	max-width: 1200px;
}

.article {
  margin: 0 0 10px 0;
  background: #FFF;
  padding: 12px 15px;
}

.article p {
  font-weight: 600 !important;
}

.title-border {
  border: 1px solid #e7d8af;
}

.details .act-item h2 {
  color: #b38707;
  font-size: 20px;
  line-height: 43px;
  font-weight: 600;
}

.act-detail .act-item {
  background: #FFF;
  padding: 0px 25px 40px 25px;
}

.act-detail .act-item .desc p {
  font-size: 16px;
}

.details p {
  margin-bottom: 0px;
}

.desc-text p {
  font-size: 16px;
  font-family: Arial, helvetica, sans-serif !important;
  font-weight:400;
}

h2.blog-title {
  font-size: 16px;
  font-weight: 600;
  color: #b38707;
  padding-bottom: 10px;
}

</style>
